import React from 'react';
import { observer } from 'mobx-react';

import Container from '@material-ui/core/Container';

import AccountStepper from 'components/AccountStepper/AccountStepper';
import Dashboard from 'pages/Accounts/Detail/Dashboard/Dashboard';
import Plan from 'pages/Accounts/Edit/Plan/Plan';
import Fees from 'pages/Accounts/Edit/Fees/Fees';
import SingleFees from 'pages/Accounts/Edit/Fees/SingleFees';
import RegularFees from 'pages/Accounts/Edit/Fees/RegularFees';
import Investments from 'pages/Accounts/Edit/Investments/Investments';
import Development from 'pages/Accounts/Detail/Development/Development';
import Reports from 'pages/Accounts/Detail/Reports/Reports';
import Movements from 'pages/Accounts/Detail/Movements/Movements';
import Documents from 'pages/Accounts/Detail/Documents/Documents';
import Withdraw from 'pages/Accounts/Detail/Withdrawal/Withdraw';
import Adjustments from 'pages/Accounts/Detail/Withdrawal/Adjustments';
import SignedDocuments from 'pages/Accounts/Detail/SignedDocuments/SignedDocuments';
import Profile from 'pages/Accounts/Profile/Profile';
import SignatureDocuments from 'pages/Accounts/Detail/Signature/Signature';
import Overview from 'pages/Accounts/Detail/Overview/Overview';
import PersonalData from 'pages/Accounts/Edit/Contract/PersonalData/PersonalData';
import DetailBankAccounts from 'pages/Accounts/Profile/BankAccounts/BankAccounts';
import Transfer from 'pages/Accounts/Detail/Transfer/Transfer';
import TransferTarget from 'pages/Accounts/Detail/Transfer/TransferTarget';

import { DetailPage } from 'pages/Accounts/Detail/enums';
import { AccountDetailStore } from 'stores/account/accountDetailStore';

interface Props {
  store: AccountDetailStore;
  accountId: string;
  userMode: boolean;
  tab: DetailPage | undefined;
  setTab: Function;
  setDepositsModalOpen: (open: boolean) => void;
  refreshTimestamp: string;
}

const ContentSections: React.FC<Props> = ({
  store,
  accountId,
  tab,
  setTab,
  setDepositsModalOpen,
  userMode,
  refreshTimestamp,
}) => {
  const { currentPage, flow, dataProcessed } = store;

  if (!dataProcessed && !userMode) return null;

  return (
    <>
      {flow && flow.steps.some((step) => step.id === currentPage) && (
        <Container maxWidth="md">
          <AccountStepper
            steps={flow.steps.map((step) => ({ id: step.id, title: step.title }))}
            activeId={currentPage}
            onChange={store.setFlowStep}
          />
        </Container>
      )}

      {currentPage === DetailPage.PROFILE && <Profile accountDetailStore={store} />}
      {currentPage === DetailPage.PERSONAL_DATA && (
        <PersonalData detailStore={store} readOnly={false} mode={store.mode} />
      )}
      {currentPage === DetailPage.BANK_ACCOUNTS && (
        <DetailBankAccounts tabChange={setTab} readOnly={false} accountDetailStore={store} />
      )}

      {currentPage === DetailPage.WITHDRAW && <Withdraw accountDetailStore={store} />}
      {currentPage === DetailPage.WITHDRAW_ADJUST && <Adjustments accountDetailStore={store} />}
      {currentPage === DetailPage.OVERVIEW && <Overview accountDetailStore={store} />}

      {currentPage === DetailPage.ID && (
        <Investments
          goFwd={store.goFwd}
          readOnly={!store.editMode}
          userMode={userMode}
          accountId={accountId}
          detailStore={store}
        />
      )}

      {currentPage === DetailPage.PLAN && (
        <Plan
          detailMode={true}
          userMode={userMode}
          detailStore={store}
          refreshTimestamp={refreshTimestamp}
          planBasicData={store.basicData}
        />
      )}

      {currentPage === DetailPage.FEES && <Fees detailMode accountCurrency={store.currency} readOnly />}
      {currentPage === DetailPage.SINGLE_FEES && (
        <SingleFees detailStore={store} accountCurrency={store.currency} detailMode readOnly={false} />
      )}
      {currentPage === DetailPage.REGULAR_FEES && (
        <RegularFees detailStore={store} accountCurrency={store.currency} detailMode readOnly={false} />
      )}

      {currentPage === DetailPage.TRANSFER && <Transfer accountDetailStore={store} />}
      {currentPage === DetailPage.TRANSFER_TARGET && <TransferTarget accountDetailStore={store} />}

      {currentPage === DetailPage.DOCS && <Documents accountDetailStore={store} />}

      {currentPage === DetailPage.DASHBOARD && (
        <Dashboard
          userMode={userMode}
          currency={store.currency}
          accountId={accountId}
          handleDepositModal={setDepositsModalOpen}
          detailStore={store}
          refreshTimestamp={refreshTimestamp}
        />
      )}
      {currentPage === DetailPage.HISTORY && (
        <Development accountId={accountId} detail={store.data.detail} userMode={userMode} />
      )}
      {currentPage === DetailPage.REPORTS && <Reports accountId={accountId} />}
      {currentPage === DetailPage.MOVEMENTS && <Movements accountId={accountId} mode={store.mode} />}
      {currentPage === DetailPage.SIGNED_DOCUMENTS && <SignedDocuments accountId={accountId} />}
      {currentPage === DetailPage.SIGNATURE && <SignatureDocuments accountId={accountId} />}
    </>
  );
};

export default observer(ContentSections);
